import React, { useState, useEffect, useContext } from 'react';
import { Navigate, useLocation, Link } from "react-router-dom";
import { Card, CardBody, Container } from 'reactstrap';
import { Lock } from 'lucide-react';
import MainContext from '../Components/Contexts/MainContext';
// #185: roles now arrive as a prop from Routes/index.js, so the route list is no longer looked up here.
// import { authProtectedRoutes } from './allRoutes';
import VerticalLayout from '../Layouts/index';
import { JAVA_BACKEND_API_URL, JAVA_BACKEND_USER_PASS } from '../Components/Contexts/BackendConstants';
import axios from 'axios';
import { pageAccessGroupIdOf } from './pageAccess';
import { isStaffGroupInfo } from '../services/firmaSessionService';
import { getMyPermissions } from '../services/rbacService';
import PortalKapisi from './PortalKapisi';
import OnayKapisi from './OnayKapisi';
import { useOnizleme } from '../services/portalOnizleme';
import { useTranslation } from "react-i18next";

// #302: the route's roles are checked against the role whose PAGES this login gets — a role created
// in Yetki Yönetimi borrows its template's (see pageAccess.js). Built-in roles: their own id, as before.
// WAS:
// const readGroupId = (groupInfo) => {
//   const id = Number(groupInfo?.id);
//   return Number.isFinite(id) ? id : null;
// };
const readGroupId = (groupInfo) => pageAccessGroupIdOf(groupInfo);

// A logged-in user whose group may not open this route (#185). Rendered in place rather than
// redirected: /Dashboard would loop for groups 12 and 14-18, since HomeRedirect sends every
// non-firma group to /FdsSaha, whose roles leave them out.
const YetkisizErisim = () => {
  const { t } = useTranslation("components");
  return (
  <VerticalLayout>
    <div className="page-content">
      <Container fluid>
        <Card className="border-0 shadow-sm mt-3">
          <CardBody className="text-center py-5">
            <Lock size={40} className="text-muted mb-2" />
            <h5 className="mb-1">{t("authProtected.buSayfayaErisimYetkinizYok")}</h5>
            <p className="text-muted small mb-3">{t("authProtected.kullaniciGrubunuzBuSayfayiAcamiyor")}</p>
            <Link to="/Dashboard" className="btn btn-primary btn-sm">{t("authProtected.anaSayfayaDon")}</Link>
          </CardBody>
        </Card>
      </Container>
    </div>
  </VerticalLayout>
  );
};

// #307: a company login on a staff page. Same notice, pointing at the portal's own home.
const FirmaPortaliUyarisi = () => {
  const { t } = useTranslation("components");
  return (
  <VerticalLayout>
    <div className="page-content">
      <Container fluid>
        <Card className="border-0 shadow-sm mt-3">
          <CardBody className="text-center py-5">
            <Lock size={40} className="text-muted mb-2" />
            <h5 className="mb-1">{t("authProtected.buSayfaSakaPersonelineAcik")}</h5>
            <p className="text-muted small mb-3">{t("authProtected.hesabinizFirmaPortaliniKullaniyor")}</p>
            <Link to="/Firma/Anasayfa" className="btn btn-primary btn-sm">{t("authProtected.firmaPortalinaGit")}</Link>
          </CardBody>
        </Card>
      </Container>
    </div>
  </VerticalLayout>
  );
};

const readStaff = () => {
  try {
    return isStaffGroupInfo(JSON.parse(localStorage.getItem('groupInfo')));
  } catch {
    return false;
  }
};

// const AuthProtected = ({ children }) => {
// roles: the rendered route's own roles, passed by Routes/index.js (#185). Undefined = any logged-in user.
// WAS (until #307): const AuthProtected = ({ children, roles }) => {
//
// #307 (2026-10-10): every route also has a SIDE — who it is for — decided by the backend's staff rule
// (groupInfo.staff: SuperAdmin or an is_denetmen = 1 role), never by a list of role ids:
//   side "firma" — the company portal: every non-staff login, whatever its role (9, 19, the Organizatör
//                  roles 14-18, a role created later). Staff are refused.
//   side "any"   — both. Company logins pass; staff still go through `roles` when the route has one.
//   no side      — a staff page (the default). Company logins are refused, then `roles` applies as before.
// permission: a permission key from GET /rbac/me (e.g. 'rbac.manage'); root holds every key. When set,
// the route needs it on top of the side check -- the first route gated by permission instead of role
// ids (suggestion 4, 2026-10-10). The backend checks every call itself; this only decides the page.
// #311 (2026-10-11): modul / modulMin — a company-portal route's module (PortalKapisi): refused when the
// active interface set does not reach it, and every firma route waits for the portal context, so a
// company whose firm cannot be resolved sees an error instead of the test decorator's portal.
// WAS (until #311): const AuthProtected = ({ children, roles, side, permission }) => {
// WAS (until #446): const AuthProtected = ({ children, roles, side, permission, modul, modulMin }) => {
// #446: alan — the organizer area (STANDLAR, ...) a page belongs to; PortalKapisi asks for its tier.
const AuthProtected = ({ children, roles, side, permission, modul, modulMin, alan }) => {
  // true once the token has been checked and is valid. The ROLE check no longer lives in this flag (#185).
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [groupId, setGroupId] = useState(() => {
    try {
      return readGroupId(JSON.parse(localStorage.getItem('groupInfo')));
    } catch {
      return null;
    }
  });
  const [staff, setStaff] = useState(readStaff);
  // null while unknown (or no permission needed), then true / false.
  const [permissionOk, setPermissionOk] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const token = localStorage.getItem("token");
  // #411: { firmaId, firmaAdi, arayuz } while a staff login previews a company's portal (read-only), else null.
  const onizleme = useOnizleme();
  // A staff login previewing the portal needs page.firma on top of being staff (the backend asks for it too, GET /portal/context).
  // WAS (until #411): only the route's own `permission`.
  const gerekliIzin = permission || (staff && side === 'firma' && onizleme ? 'page.firma' : null);

  const { fetchGroupInfo } = useContext(MainContext);

  // Was hardcoded to the production gateway regardless of REACT_APP_API_URL, so a
  // token issued by a local backend login would always 401 here and bounce back to
  // /login — see util/knowledgebase/local-backend-testing.md. JAVA_BACKEND_API_URL
  // already falls back to production when no local .env is set, so this is
  // behavior-neutral for prod and fixes local-backend testing.
  const apiurll = JAVA_BACKEND_API_URL;
  // #429: the gateway's Basic credential is the ONE shared constant (BackendConstants.js); this file carried its own copy of it.
  const authHeader = 'Basic ' + btoa(JAVA_BACKEND_USER_PASS);

  // 2026-10-26: the token is checked on GET /rbac/me, which reads our own database (any logged-in user reads their own, 401
  // otherwise). The salon list it replaces is BetterStands, read from the old PHP database: a Php-Mysql that was down or not
  // configured (fds.phpdb.*, backend #429) sent every login back to /login.
  // WAS (until 2026-10-26):
  // const fetchSalons = async (fairId) => {
  //   const useFairId = fairId === undefined ? 290 : fairId;
  //   try {
  //     const response = await axios.get(
  //       `${apiurll}/betterStands/salonsByFuarId/${useFairId}`,
  //       {
  //         headers: {
  //           Authorization: authHeader,
  //           "X-token": token,
  //           "X-user-id": localStorage.getItem("userId"),
  //         },
  //       }
  //     );
  //
  //     if (response.data && response.data.salons) {
  //       return true;
  //     } else {
  //       console.error("Salon bilgisi alınamadı:", response.data);
  //       return false;
  //     }
  //   } catch (error) {
  //     console.error("Salonlar alınırken hata oluştu veya token geçersiz:", error);
  //     return false;
  //   }
  // };
  const checkToken = async () => {
    try {
      const response = await axios.get(`${apiurll}/rbac/me`, {
        headers: {
          Authorization: authHeader,
          "X-token": token,
          "X-user-id": localStorage.getItem("userId"),
        },
      });

      if (response.data && response.data.userId != null) {
        return true;
      }
      console.error("Oturum doğrulanamadı:", response.data);
      return false;
    } catch (error) {
      console.error("Oturum doğrulanırken hata oluştu veya token geçersiz:", error);
      return false;
    }
  };

  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        let groupInfo = JSON.parse(localStorage.getItem('groupInfo'));

        if (user && user.id && token) {
          if (!groupInfo) {
            groupInfo = await fetchGroupInfo(user.id);
            localStorage.setItem('groupInfo', JSON.stringify(groupInfo));
          }

          setGroupId(readGroupId(groupInfo));
          setStaff(isStaffGroupInfo(groupInfo));

          // WAS (until 2026-10-26): const tokenValid = await fetchSalons(290);
          const tokenValid = await checkToken();
          if (!tokenValid) {
            setIsAuthorized(false);
          } else {
            // #185: string equality never matched a :param route (e.g. /Management/Firmalar/Profil/:firmaId
            // vs /Management/Firmalar/Profil/49858), so currentRoute came back undefined and every such route
            // was open to any logged-in user. The role check is now made at render time from the roles prop.
            // const { authProtectedRoutes } = await import("./allRoutes");
            //
            // const currentRoute = authProtectedRoutes.find(
            //     (route) => route.path.toLowerCase() === location.pathname.toLowerCase()
            // );
            //
            // if (currentRoute && currentRoute.roles) {
            //   const userGroupId = groupInfo.id;
            //   setIsAuthorized(currentRoute.roles.includes(userGroupId));
            // } else {
            //   setIsAuthorized(true);
            // }
            setIsAuthorized(true);
          }
        }
      } catch (error) {
        console.error("Authorization error:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuthorization();
  }, [token, location.pathname, fetchGroupInfo]);

  // #307: a permission-gated route asks GET /rbac/me once per user (rbacService caches it).
  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    // WAS (until #411): if (!permission || !token) return undefined;
    if (!gerekliIzin || !token) return undefined;
    let ignore = false;
    async function run() {
      try {
        const me = await getMyPermissions();
        // WAS (until #411): if (!ignore) setPermissionOk(Boolean(me?.root) || (me?.permissions || []).includes(permission));
        if (!ignore) setPermissionOk(Boolean(me?.root) || (me?.permissions || []).includes(gerekliIzin));
      } catch {
        if (!ignore) setPermissionOk(false);
      }
    }
    run();
    return () => { ignore = true; };
    // WAS (until #411): }, [permission, token]);
  }, [gerekliIzin, token]);

  if (loading) {
    return <div>Loading...</div>;
  }

  // return isAuthorized ? children : <Navigate to="/login" />;
  if (!isAuthorized) return <Navigate to="/login" />;
  // Checked on every render, not after the async token check: on in-app navigation this component
  // is reused, so a forbidden page would otherwise mount and fire its API calls first (#185).
  // WAS (until #307): if (Array.isArray(roles) && !roles.includes(groupId)) return <YetkisizErisim />;
  if (side === 'firma') {
    // WAS (until #411): if (staff) return <YetkisizErisim />;
    // #411: staff may open the company portal only as a read-only PREVIEW of one company (Firma Detay).
    if (staff && !onizleme) return <YetkisizErisim />;
  } else if (!staff) {
    if (side !== 'any') return <FirmaPortaliUyarisi />;
  } else if (Array.isArray(roles) && !roles.includes(groupId)) {
    return <YetkisizErisim />;
  }
  // WAS (until #325): if (permission) {
  // #325: on a side "any" page the permission is a STAFF page permission (page.*); a company login passed by
  // side and holds only portal permissions, so it is not asked for one.
  // WAS (until #411): if (permission && (staff || side !== 'any')) {
  if (gerekliIzin && (staff || side !== 'any')) {
    if (permissionOk === null) return <div>Loading...</div>;
    if (!permissionOk) return <YetkisizErisim />;
  }
  // WAS (until #311): return children;
  // WAS (until #411): if (!staff && side === 'firma') {
  const sayfa = ((!staff || onizleme) && side === 'firma')
    // WAS (until #446): ? <PortalKapisi modul={modul} min={modulMin}>{children}</PortalKapisi>
    ? <PortalKapisi modul={modul} min={modulMin} alan={alan}>{children}</PortalKapisi>
    : children;
  // #540: a company login (Katılımcı / Dekoratör) answers the consent texts before ANY portal page, side "any" ones included.
  // Staff, and a staff preview of a company, never reach the gate: OnayContext decides who is asked (services/onayGecidi.js).
  // WAS: return the page as it is.
  return staff ? sayfa : <OnayKapisi>{sayfa}</OnayKapisi>;
};

export { AuthProtected };